import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import {
  JSX, useCallback, useEffect, useMemo, useState,
} from 'react';
import dayjs from 'dayjs';
import { Box } from '@mui/material';
import { ChevronDownIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import CustomDataGrid from '@/components/Common/DataGrid';
import { corePalette } from '@/themes/colours';
import CustomChip from '@/components/Common/Chip';
import { IWispResultsRow } from '@/types/PatientDashboard/ResultsView.types';
import { FilesButton } from '@/components/Common/FilesButton';

interface IAccordionRow {
  rowKind: 'accordion';
  assay: string;
  isExpanded: boolean;
  childRows: IWispResultsRow[];
}

interface IDataRow extends IWispResultsRow {
  rowKind: 'result';
}

type IWispTableRow = IAccordionRow | IDataRow;

interface IProps {
  rows: IWispResultsRow[];
}

// An assay header stretches across every column but the last, which carries that
// group's expand/collapse chevron instead. Keep in step with the column count.
const ACCORDION_HEADER_COLUMN_SPAN = 15;

function MissingValue(): JSX.Element {
  return (
    <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
      -
    </CustomTypography>
  );
}

// Check `=== null`, not truthiness: 0 is a real count and must show as 0,
// only a missing count shows a dash. The CF-DNA cells below use a truthy
// check, so 0 shows a dash there.
function CountCell({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <MissingValue />;

  return (
    <CustomChip
      label={value.toLocaleString()}
      fontWeight="bold"
      backgroundColour={corePalette.blue30}
      colour={corePalette.blue300}
    />
  );
}

export default function WispResultsTable({
  rows,
}: IProps): JSX.Element {
  const [tableRows, setTableRows] = useState<IWispTableRow[]>([]);

  const handleAccordionToggle = useCallback((row: IAccordionRow) => {
    setTableRows((prev) => {
      const newRow: IAccordionRow = { ...row, isExpanded: !row.isExpanded };
      if (newRow.isExpanded) {
        const index = prev.findIndex((r) => r.rowKind === 'accordion' && r.assay === row.assay);
        const newArr = [...prev];
        newArr.splice(
          index + 1,
          0,
          ...newRow.childRows.map<IDataRow>((r) => ({ ...r, rowKind: 'result' })),
        );

        return newArr.map((r) => (r.rowKind === 'accordion' && r.assay === newRow.assay ? newRow : r));
      }

      return prev
        .filter((r) => r.rowKind !== 'result' || !newRow.childRows.some((c) => c.biosampleId === r.biosampleId))
        .map((r) => (r.rowKind === 'accordion' && r.assay === newRow.assay ? newRow : r));
    });
  }, []);

  const cols: GridColDef<IWispTableRow>[] = useMemo(() => [
    {
      field: 'biosampleId',
      headerName: 'Biosample ID',
      minWidth: 200,
      flex: 2,
      colSpan: (value, row) => (row.rowKind === 'accordion' ? ACCORDION_HEADER_COLUMN_SPAN : 1),
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') {
          return (
            <CustomTypography variant="label" fontWeight="bold">
              {`${row.assay} (${row.childRows.length})`}
            </CustomTypography>
          );
        }
        return (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            {row.biosampleId}
          </CustomTypography>
        );
      },
    },
    {
      field: 'biomaterialName',
      headerName: 'Biomaterial name',
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            {row.biomaterialName}
          </CustomTypography>
        );
      },
    },
    {
      field: 'collectionDate',
      headerName: 'Collection date',
      type: 'date',
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) => {
        if (row.rowKind === 'accordion') return null;
        const d = row.collectionDate;
        if (!d) return null;
        const parsed = new Date(d);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      },
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return (
          <CustomChip
            label={dayjs(row.collectionDate).format('DD/MM/YYYY')}
            fontWeight="bold"
            backgroundColour={corePalette.grey10}
            border={`1px solid ${corePalette.grey50}`}
          />
        );
      },
    },
    {
      field: 'detected',
      headerName: 'Detected',
      minWidth: 140,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        // A missing result is not a negative one.
        if (row.detected === null) return <MissingValue />;
        return (
          <CustomChip
            label={row.detected ? 'DETECTED' : 'NOT DETECTED'}
            fontWeight="bold"
            backgroundColour={corePalette.orange10}
            colour={corePalette.orange200}
          />
        );
      },
    },
    {
      field: 'snvPurity',
      headerName: 'Purity (%)',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        if (row.snvPurity === null) return <MissingValue />;
        return (
          <CustomChip
            label={row.snvPurity.toFixed(2)}
            fontWeight="bold"
            backgroundColour={corePalette.blue30}
            colour={corePalette.blue300}
          />
        );
      },
    },
    {
      field: 'lbLod',
      headerName: 'LB Lod (%)',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        if (row.lbLod === null) return <MissingValue />;
        return (
          <CustomChip
            label={row.lbLod.toFixed(2)}
            fontWeight="bold"
            backgroundColour={corePalette.blue30}
            colour={corePalette.blue300}
          />
        );
      },
    },
    {
      field: 'variants',
      headerName: 'Variants',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return <CountCell value={row.variants} />;
      },
    },
    {
      field: 'fragments',
      headerName: 'Fragments',
      minWidth: 110,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return <CountCell value={row.fragments} />;
      },
    },
    {
      field: 'vdDuplex',
      headerName: 'VD Duplex',
      minWidth: 110,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return <CountCell value={row.vdDuplex} />;
      },
    },
    {
      field: 'analysisFormedByTmrAt',
      headerName: 'Analysis formed by tumour at',
      type: 'date',
      minWidth: 50,
      flex: 2,
      sortable: false,
      valueGetter: (value, row) => {
        if (row.rowKind === 'accordion') return null;
        const d = row.analysisFormedByTmrAt?.tumourCollectionDate;
        if (!d) return null;
        const parsed = new Date(d);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      },
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        const { tumourCollectionDate, event } = row.analysisFormedByTmrAt;
        return (
          <Box display="flex" gap="8px" flex="1" minWidth={0}>
            <CustomChip
              label={dayjs(tumourCollectionDate).format('DD/MM/YYYY')}
              fontWeight="bold"
              backgroundColour={corePalette.grey10}
              border={`1px solid ${corePalette.grey50}`}
            />
            <CustomTypography
              fontWeight="medium"
              variant="bodyRegular"
              truncate
            >
              {`(${event})`}
            </CustomTypography>
          </Box>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type (subtype)',
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            {row.type}
          </CustomTypography>
        );
      },
    },
    {
      field: 'containerType',
      headerName: 'Container Type',
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            {row.containerType || '-'}
          </CustomTypography>
        );
      },
    },
    {
      field: 'cfdnaYield',
      headerName: 'CF-DNA Yield',
      minWidth: 105,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return row.cfdnaYield ? (
          <CustomChip
            label={row.cfdnaYield.toFixed(2)}
            fontWeight="bold"
            backgroundColour={corePalette.blue30}
            colour={corePalette.blue300}
          />
        ) : (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            -
          </CustomTypography>
        );
      },
    },
    {
      field: 'cfdnaFraction',
      headerName: 'CF-DNA Fraction',
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return row.cfdnaFraction ? (
          <CustomChip
            label={row.cfdnaFraction.toFixed(2)}
            fontWeight="bold"
            backgroundColour={corePalette.blue30}
            colour={corePalette.blue300}
          />
        ) : (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            -
          </CustomTypography>
        );
      },
    },
    {
      field: 'panel',
      headerName: 'Panel used',
      minWidth: 110,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') return null;
        return (
          <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
            {row.panel || '-'}
          </CustomTypography>
        );
      },
    },
    {
      field: 'files',
      headerName: 'Files',
      maxWidth: 80,
      minWidth: 80,
      renderCell: ({ row }) => {
        if (row.rowKind === 'accordion') {
          return (
            <Box display="flex" alignItems="center" gap="4px">
              <CustomTypography variant="bodySmall" fontWeight="bold">
                {row.isExpanded ? 'Hide' : 'Show'}
              </CustomTypography>
              <ChevronDownIcon
                width="22px"
                height="20px"
                style={{
                  transform: row.isExpanded ? 'rotate(180deg)' : undefined,
                  transition: 'all 0.5s cubic-bezier(.19, 1, .22, 1)',
                }}
              />
            </Box>
          );
        }
        return row.files.length > 0 ? (
          <FilesButton files={row.files} entityName={row.biosampleId} />
        ) : null;
      },
    },
  ], []);

  useEffect(() => {
    const groups = rows.reduce<Record<string, IWispResultsRow[]>>((acc, row) => {
      const key = row.assay;
      return { ...acc, [key]: [...(acc[key] ?? []), row] };
    }, {});

    setTableRows(Object.entries(groups).flatMap(([assay, childRows]) => [
      {
        id: assay,
        rowKind: 'accordion' as const,
        assay,
        isExpanded: true,
        childRows,
      },
      ...childRows.map<IDataRow>((r) => ({ ...r, rowKind: 'result' as const })),
    ]));
  }, [rows]);

  return (
    <Box>
      <CustomDataGrid
        columns={cols}
        rows={tableRows}
        autoHeight
        hideCountSummary
        onRowClick={(params: GridRowParams<IWispTableRow>) => {
          if (params.row.rowKind === 'accordion') handleAccordionToggle(params.row);
        }}
        getRowHeight={(params) => (params.model.rowKind === 'accordion' ? 40 : 'auto')}
        getCellSx={(row) => (row.rowKind === 'accordion' ? { backgroundColor: corePalette.grey30, cursor: 'pointer' } : {})}
        disableColumnSorting
        disableColumnFilter
      />
    </Box>
  );
}
